import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun, ChevronDown } from "lucide-react";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const dropdownRef = useRef(null);

  const toggleDarkMode = () => {
    const newDark = !dark;
    setDark(newDark);
    localStorage.setItem("theme", newDark ? "dark" : "light");
    document.documentElement.classList.toggle("dark", newDark);
  };

  useEffect(() => {
    if (dark) document.documentElement.classList.add("dark");
  }, [dark]);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const location = useLocation();
  useEffect(() => {
    closeMenu();
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="bg-gray-950/95 backdrop-blur text-white sticky top-0 z-50 border-b border-yellow-400/20 transition duration-300">
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">

        <Link to="/" className="text-xl font-extrabold tracking-tight hover:text-yellow-400 transition">
          The <span className="text-yellow-400">Transporter</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <div ref={dropdownRef} className="relative">
            <button
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm text-gray-300 hover:text-yellow-400 transition focus:outline-none"
            >
              MAC Bus Services <ChevronDown className="h-4 w-4" />
            </button>
            <div
              className={`absolute left-0 top-full mt-3 w-56 bg-gray-900 border border-yellow-400/20 rounded-xl shadow-2xl overflow-hidden transform transition-all duration-200 ease-out origin-top ${
                dropdownOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
              }`}
            >
              {[
                { to: "/ayeletsride", label: "Ride Request", bold: true },
                { to: "/about", label: "About Us" },
                { to: "/now-hiring", label: "Now Hiring" },
                { to: "/routes", label: "View Routes" },
                { to: "/invest", label: "Invest in Mobility" },
                { to: "/community", label: "Our Future" },
                { to: "/sponsor", label: "Partner With Us" },
                { to: "/shuttle-pricing", label: "Shuttle Pricing" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`block px-4 py-2.5 text-sm text-gray-300 hover:bg-yellow-400 hover:text-gray-950 transition ${
                    item.bold ? "font-bold" : ""
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/ayeletsride"
            className="bg-yellow-400 text-gray-950 text-sm font-bold px-5 py-2.5 rounded-full hover:bg-yellow-300 transition"
          >
            Book a Ride
          </Link>

          <button
            onClick={toggleDarkMode}
            className="text-gray-400 hover:text-yellow-400 transition focus:outline-none"
            title="Toggle dark mode"
            aria-label="Toggle dark mode"
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </nav>

        <button
          onClick={toggleMenu}
          className="md:hidden text-gray-300 hover:text-yellow-400 transition focus:outline-none"
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out bg-gray-950 border-t border-yellow-400/10 ${
          isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-6 py-4 flex flex-col gap-1">
          {[
            { to: "/ayeletsride", label: "Ride Request", bold: true },
            { to: "/about", label: "About Us" },
            { to: "/now-hiring", label: "Now Hiring" },
            { to: "/routes", label: "View Routes" },
            { to: "/invest", label: "Invest in Mobility" },
            { to: "/community", label: "Our Future" },
            { to: "/sponsor", label: "Partner With Us" },
            { to: "/shuttle-pricing", label: "Shuttle Pricing" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={closeMenu}
              className={`py-2.5 px-3 rounded-lg text-sm text-gray-300 hover:bg-yellow-400/10 hover:text-yellow-400 transition ${
                item.bold ? "font-bold" : ""
              }`}
            >
              {item.label}
            </Link>
          ))}
          <div className="border-t border-white/10 mt-3 pt-3 flex items-center justify-between">
            <Link
              to="/ayeletsride"
              onClick={closeMenu}
              className="bg-yellow-400 text-gray-950 text-sm font-bold px-5 py-2.5 rounded-full hover:bg-yellow-300 transition"
            >
              Book a Ride
            </Link>
            <button
              onClick={() => { toggleDarkMode(); closeMenu(); }}
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-yellow-400 focus:outline-none transition"
            >
              {dark ? <><Sun size={16} /> Light</> : <><Moon size={16} /> Dark</>}
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}
